import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  allowsAnalytics,
  ConsentChoice,
  readConsentCookie,
} from '../../shared/utils/consent-cookie';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type ConsentState = 'granted' | 'denied';

const NOTHING_ACCEPTED: ConsentChoice = { functional: false, statistical: false, marketing: false };

/**
 * One GA4 ecommerce item, as the container's GA4 tag forwards it.
 *
 * Same field list as the legacy webshop's `Ga4EcommerceItem`, because the same GTM variables read
 * it. `item_category3` and up are optional for the same reason they are there: a trip with no
 * destination hierarchy fills fewer levels, and an empty string is a category in GA4 while an
 * absent key is not.
 */
export interface Ga4EcommerceItem {
  item_id: string;
  item_name: string;
  discount?: number;
  item_category: string;
  item_category2: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

/** The scalar half of a GA4 `purchase`, matching the webshop's `Ga4PurchaseParams`. */
export interface Ga4PurchaseParams {
  transactionId: string;
  revenue: number;
  affiliation: string;
  coupon: string;
  shippingCost: number;
}

/** One product of a legacy Enhanced Ecommerce push. Loosely typed, exactly as in the webshop. */
export interface EecProduct {
  id: string;
  name: string;
  category: string;
  variant: string;
  quantity: number;
  price: string;
}

/**
 * How long a page view waits after `NavigationEnd` before it is sent. Long enough for the change
 * detection pass that follows a navigation to have run, short enough that a visitor cannot
 * navigate on again in between; it also collapses superseded navigations into the page the visitor
 * actually ended up on. Borrowed wholesale from `apps/groups`, which measures the same way.
 */
export const PAGE_VIEW_SETTLE_MS = 50;

/**
 * Consent Mode v2 signals for a choice.
 *
 * Every storage type Google defines is named on purpose: one left out defaults to `granted`, so an
 * omission hands out more than the visitor agreed to. Functionality and security are granted either
 * way — that is the necessary category, which cannot be switched off.
 *
 * `ad_*` follow the marketing category and `analytics_storage` follows {@link allowsAnalytics},
 * which is how the legacy webshop maps the same three switches. The container's tags are built on
 * that mapping; diverging here would measure one visitor two different ways depending on which
 * subdomain they happened to be on.
 */
function consentSignals(choice: ConsentChoice): Record<string, ConsentState> {
  const analytics: ConsentState = allowsAnalytics(choice) ? 'granted' : 'denied';
  const marketing: ConsentState = choice.marketing ? 'granted' : 'denied';

  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    personalization_storage: marketing,
    analytics_storage: analytics,
    functionality_storage: 'granted',
    security_storage: 'granted',
  };
}

/**
 * Google Tag Manager for the funnel.
 *
 * Loads the SAME container as the rest of imt.fi (`GTM-M4HSJN5`) rather than one of its own, and
 * speaks the same dataLayer contract as `apps/webshop`'s `GoogleTagManagerService`: identical event
 * names, identical variable names, identical ecommerce shape. That is deliberate and it is the
 * whole design — every Meta, Google Ads, GA4 and Custobar tag in that container keeps firing
 * unchanged as purchases move from the webshop to the funnel, and the conversion history stays
 * comparable across the cutover. A separate container, or tidier event names, would mean rebuilding
 * every tag and losing continuity for nothing.
 *
 * Consent is not asked for here. It is read from the `.imt.fi` cookie the webshop's banner
 * publishes (see `shared/utils/consent-cookie.ts`), because the customer reaches this app through a
 * top-level redirect from imt.fi, where they have already answered. With no cookie the container
 * still loads but every signal stays denied, so GA4 sends a cookieless ping and the ad tags store
 * nothing — the conservative direction, and the reason a funnel-side banner is a follow-up rather
 * than a blocker.
 *
 * Internal (ERP staff) traffic is NOT excluded, unlike in `apps/groups`. During the funnel's
 * staff-only phase that traffic is nearly all of it, and the webshop has always reported staff
 * purchases too — with `affiliation: 'IM Sisäinen'` and `loggedInToErp: 'yes'` beside them, which
 * is what any container-side filtering keys on. Dropping them here would both break revenue parity
 * with the webshop and make the funnel look like it sells nothing.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private router = inject(Router);

  private initializedState = signal(false);

  /**
   * Whether the container has booted, and so whether {@link pushEvent} will do
   * anything at all.
   *
   * A signal rather than a plain flag because {@link init} is called once the
   * webshop session is known, which can be a tick or two *after* a component
   * has decided it has something to report. A reporter that reads this is
   * re-run when it flips and gets its hit out; one that latches on the push
   * alone loses that hit for the whole visit. Stays false for good when
   * analytics is switched off or no container id is configured — there is
   * nothing to wait for then, and no hit to lose.
   */
  readonly ready = this.initializedState.asReadonly();

  /**
   * Whether this browser is signed in to the ERP, as far as reporting is concerned. Set by
   * {@link init}'s caller once the webshop session is known, and pushed with every page view under
   * the same variable name the webshop uses.
   */
  private loggedInToErp = false;

  /**
   * The last page measured, so an in-app navigation can name the page it came from. GA4 reads a
   * referrer off the browser only on a real document load, and a single-page app has one of those
   * per visit.
   */
  private lastPageLocation: string | null = null;

  /** The consent signals last sent, so an unchanged answer is not re-pushed. */
  private appliedConsent: string | null = null;

  /**
   * Boots the container with the visitor's stored choice, or fully denied when they have not
   * answered. Safe to call more than once; only the first call does anything.
   *
   * Order matters and matches the webshop's `init()`: defaults first, then any known choice, then
   * the script. Defaults set after the container loads would arrive too late to gate the tags they
   * exist to gate.
   *
   * @param loggedInToErp whether this session is an ERP staff session
   */
  init(loggedInToErp: boolean): void {
    this.loggedInToErp = loggedInToErp;

    if (this.initializedState()) return;
    if (!environment.analytics.enabled || !environment.analytics.gtmContainerId) return;

    this.initializedState.set(true);
    this.ensureGtag();

    // Both consent commands have to sit in the data layer before gtm.js is fetched, or the
    // container boots and the Google tag fires its first hit unrestricted.
    this.setDefaultConsentSettings();
    this.refreshConsent();

    this.insertGtmScriptToPage();
    this.startPageViewTracking();
  }

  /**
   * Re-reads the consent cookie and applies it, without reloading the container.
   *
   * There is no banner in this app, so the answer is not given here — but it can change under us.
   * The customer can answer, or revise, the banner on imt.fi in another tab while the funnel sits
   * open on a payment page, and a visit that takes half an hour would otherwise keep measuring them
   * against whatever the cookie said at boot. Called on every navigation, which is as often as it
   * is worth asking.
   *
   * Sends nothing when the answer has not changed: repeated identical updates are harmless to the
   * container but make a Preview-mode session unreadable, which is where these get verified.
   */
  refreshConsent(): void {
    if (!this.initializedState()) return;

    const storedConsent = readConsentCookie();
    if (!storedConsent) return;

    const signals = consentSignals(storedConsent);
    const fingerprint = JSON.stringify(signals);
    if (fingerprint === this.appliedConsent) return;
    this.appliedConsent = fingerprint;

    this.ensureGtag();
    window.gtag('consent', 'update', signals);
  }

  pushEvent(eventName: string, data: Record<string, unknown> = {}): void {
    if (!this.initializedState()) return;
    window.dataLayer.push({
      ...data,
      event: eventName,
    });
  }

  /**
   * Pushes an event and resolves once the container has worked through the queue — or after two
   * seconds, whichever comes first.
   *
   * A function pushed onto the data layer is invoked by the container in push order, so it resolves
   * after the tags for the preceding event have been asked to fire. The timeout is the fallback for
   * a container that never loads (blocked, offline, consent-gated to nothing), where that callback
   * would never run and the caller would wait forever. Copied in behaviour from the webshop's
   * `pushEventAsync`, whose callers use it before navigating away.
   */
  pushEventAsync(eventName: string, data: Record<string, unknown> = {}): Promise<void> {
    if (!this.initializedState()) return Promise.resolve();

    return new Promise<void>((resolve) => {
      this.pushEvent(eventName, data);
      setTimeout(resolve, 2000);
      window.dataLayer.push(() => resolve());
    });
  }

  /**
   * Reports the page view.
   *
   * `virtual_page_view`, not the container's own page-load trigger: imt.fi's GA4 page view is keyed
   * to this event, because the webshop is a single-page app that pushes it on every in-app
   * transition. A tag firing on All Pages would double-count, which is why one almost certainly
   * does not exist — and why a funnel that stayed silent here would record no page views at all.
   *
   * @param routerUrl the page's router URL, e.g. `/payment`
   */
  trackPageView(routerUrl: string, pageTitle: string = document.title): void {
    if (!this.initializedState()) return;

    const pageLocation = absoluteUrl(routerUrl);
    const referrer = this.lastPageLocation ?? sanitizedReferrer(document.referrer);
    this.lastPageLocation = pageLocation;

    this.pushEvent('virtual_page_view', {
      virtual_page_location: pageLocation,
      virtual_page_title: pageTitle,
      // Omitted rather than sent empty when a visitor lands straight here: an absent key leaves
      // GA4's own referrer handling alone, an empty one would overwrite it.
      ...(referrer ? { virtual_page_referrer: referrer } : {}),
      ...(this.loggedInToErp ? { loggedInToErp: 'yes' } : {}),
    });
  }

  /** Loyalty bonus the reservation earned. `earn_virtual_currency`, as in the webshop. */
  bonusGranted(amount: number): void {
    this.pushEvent('earn_virtual_currency', buildBonusVirtualCurrencyParams(amount));
  }

  /** Loyalty bonus the reservation spent. `spend_virtual_currency`, as in the webshop. */
  bonusUsed(amount: number): void {
    this.pushEvent('spend_virtual_currency', buildBonusVirtualCurrencyParams(amount));
  }

  /**
   * The legacy Enhanced Ecommerce purchase.
   *
   * Still sent, and not a leftover: the container's Meta and Google Ads tags read this shape, not
   * the GA4 one. Dropping it would silently remove the funnel's purchases from both ad platforms
   * while GA4 looked perfectly healthy. Mirrors the webshop's `eecPurchase` exactly.
   */
  eecPurchase(id: string, affiliation: string, revenue: string, products: EecProduct[]): void {
    this.pushEvent('eec.purchase', {
      ecommerce: {
        currencyCode: 'EUR',
        purchase: {
          actionField: {
            id,
            affiliation,
            revenue,
          },
          products,
        },
      },
    });
  }

  /**
   * The GA4 purchase.
   *
   * `totalMargin` stays outside the params object, and lands in a field called `elapsedTimeSec`,
   * because the pricing margin is a commercial secret and a named parameter would survive
   * minification as a readable label. The webshop does exactly this and says so; the container
   * reads that field as the margin metric, so the disguise is part of the contract, not a joke to
   * clean up.
   */
  purchase(
    params: Ga4PurchaseParams,
    /* keep outside params so minification obfuscates it */ totalMargin: number,
    items: Ga4EcommerceItem[],
  ): Promise<void> {
    return this.pushEventAsync('purchase', {
      elapsedTimeSec: totalMargin /* yes, not a typo, this is a disguise */,
      ecommerce: {
        currency: 'EUR',
        transaction_id: params.transactionId,
        value: params.revenue,
        affiliation: params.affiliation,
        coupon: params.coupon,
        shipping: params.shippingCost,
        items,
      },
    });
  }

  private ensureGtag(): void {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') return;

    /*
     * A gtag command reaches the container only when the pushed value is a real `arguments`
     * object — that is how GTM tells a command apart from an ordinary data layer message. Push a
     * plain array instead and it takes the legacy method-call branch, where
     * `['consent', 'default', …]` turns into a call to a `window.consent()` that does not exist
     * and the error is swallowed: the signal disappears and every visitor is measured as though
     * they had consented.
     *
     * An arrow function has no `arguments` of its own, so this has to stay a function expression.
     */
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params -- rest params rebuild the broken array
      window.dataLayer.push(arguments);
    };
  }

  private setDefaultConsentSettings(): void {
    const signals = consentSignals(NOTHING_ACCEPTED);
    this.appliedConsent = JSON.stringify(signals);
    window.gtag('consent', 'default', signals);
  }

  private startPageViewTracking(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(PAGE_VIEW_SETTLE_MS),
      )
      .subscribe((event) => {
        // Before the page view, so a hit is never sent under a consent state the visitor has since
        // moved on from.
        this.refreshConsent();
        this.trackPageView(event.urlAfterRedirects, document.title);
      });
  }

  private insertGtmScriptToPage(): void {
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const firstScript = document.getElementsByTagName('script')[0];
    if (!firstScript?.parentNode) return;

    const gtmScript = document.createElement('script');
    gtmScript.async = true;

    const { gtmContainerId, gtmAuth, gtmPreview } = environment.analytics;
    const params = new URLSearchParams({ id: gtmContainerId });
    if (gtmAuth && gtmPreview) {
      params.set('gtm_auth', gtmAuth);
      params.set('gtm_preview', gtmPreview);
      // Part of Google's own environment snippet, and the part that is easy to leave out: without
      // it the container writes its preview cookie against the wrong window and an environment can
      // stop resolving on the second page. Only ever sent alongside the two tokens.
      params.set('gtm_cookies_win', 'x');
    }
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?${params.toString()}`;

    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  }
}

/**
 * GA4 reports on `page_location` as an address, so a router path would file the same page under a
 * different row than the webshop's full URLs.
 *
 * The query string is dropped on the way, because this app's is partly credentials and partly
 * conversion payload: `accessToken` authorizes the confirmation page, and the rest of the
 * post-payment parameters carry prices and the obfuscated margin. This address becomes GA4's
 * `page_location`, readable by anyone with access to the property, and it leaves the browser even
 * when analytics storage is denied. Nothing in our reporting reads a parameter off it — the
 * purchase event carries those values deliberately and in named fields — so there is nothing on
 * the other side of the trade.
 */
function absoluteUrl(routerUrl: string): string {
  const url = new URL(routerUrl, window.location.origin);
  url.search = '';
  return url.href;
}

/**
 * The browser's referrer, with a query string taken off only when the address is our own.
 *
 * A customer arrives here from imt.fi and, on the confirmation page, from a Paytrail redirect whose
 * URL carries the reservation's access token; either can be handed on as a referrer. An address
 * from anywhere else passes through exactly as given: its query string is the campaign and search
 * attribution GA4 exists to record, and it is not ours to trim. An unparsable referrer passes
 * through for the same reason, rather than costing us the page view.
 */
function sanitizedReferrer(referrer: string): string {
  try {
    const url = new URL(referrer);
    if (url.origin !== window.location.origin) return referrer;
    url.search = '';
    return url.href;
  } catch {
    return referrer;
  }
}

function buildBonusVirtualCurrencyParams(value: number) {
  return {
    virtual_currency_name: 'Bonus',
    value,
  };
}
